import { Notification } from '@/models/registry';
import type { Notification as NotificationType } from '@/models/types';
import dayjs from 'dayjs';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';

class NotificationStore implements BaseStore<Notification, NotificationType> {
  store: Map<string, Notification> = new Map();

  storeIndexedByForeignKey: Map<string, Notification[]> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setNotificationField = <K extends keyof NotificationType>(
    notification: Notification,
    key: K,
    value: NotificationType[K],
  ) => {
    (notification as Pick<Notification, K>)[key] = value as Notification[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<NotificationType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.notifications.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.notifications.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.notifications.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.notifications.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<NotificationType>;
  }) {
    const existingNotification = this.getById(delta.record_id);
    if (!existingNotification) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof NotificationType,
      NotificationType[keyof NotificationType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingNotification[key] === newValue) {
        continue;
      }

      this.setNotificationField(existingNotification, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingNotification);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<NotificationType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<NotificationType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete') {
      if (hydrateIndexedDB) {
        // Remove from dexie
        db.notifications.delete(delta.record_id);
      }
      // Remove from MobX
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'archive') {
      return;
    }

    // Write to Dexie then MobX
    if (hydrateIndexedDB) {
      db.notifications.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  add(input: NotificationType) {
    const notification = new Notification(input);
    this.store.set(notification.id, notification);
    // Index by foreign key for O(1) lookups
    const arr = this.storeIndexedByForeignKey.get(notification.user_id) || [];
    arr.push(notification);
    this.storeIndexedByForeignKey.set(notification.user_id, arr);
  }

  upsert(notification: NotificationType) {
    const existingNotification = this.getById(notification.id);
    if (existingNotification) {
      this.replace({ id: notification.id, notification });
    } else {
      this.add(notification);
    }
  }

  replace({
    id,
    notification,
  }: {
    id: string;
    notification: NotificationType;
  }) {
    const existingNotification = this.getById(id);
    if (!existingNotification) return;

    // update the notification
    existingNotification.deal_id = notification.deal_id;
    existingNotification.user_id = notification.user_id;
    existingNotification.message = notification.message;
    existingNotification.read_at = notification.read_at;
    existingNotification.type = notification.type;
    existingNotification.metadata = notification.metadata;
    existingNotification.triggered_by_user_id =
      notification.triggered_by_user_id;

    this.save(existingNotification);
  }

  getById(id: string): Notification | undefined {
    return this.store.get(id);
  }

  markAsRead(id: NotificationType['id']): void {
    const notification = this.store.get(id);
    if (!notification) return;
    const readAt = dayjs().toISOString();
    notification.read_at = readAt;

    // DEV NOTE: we need to remove the notification to trigger recalculation of the number of unread notifications
    this.remove(id);
    this.save(notification);
  }

  markAsUnread(id: NotificationType['id']): void {
    const notification = this.store.get(id);
    if (!notification) return;
    notification.read_at = null;

    // DEV NOTE: we need to remove the notification to trigger recalculation of the number of unread notifications
    this.remove(id);
    this.save(notification);
  }

  save(notification: Notification) {
    this.store.set(notification.id, notification);

    // update the storeIndexedByForeignKey
    const arr = this.storeIndexedByForeignKey.get(notification.user_id) || [];
    // de-duplicate the notification
    const index = arr.findIndex((n) => n.id === notification.id);
    if (index !== -1) {
      arr[index] = notification;
    } else {
      arr.push(notification);
    }
    this.storeIndexedByForeignKey.set(notification.user_id, arr);
  }

  remove(id: string): void {
    const notification = this.store.get(id);
    if (!notification) return;
    this.store.delete(id);
    const userId = notification.user_id;
    const arr = this.storeIndexedByForeignKey.get(userId) || [];
    this.storeIndexedByForeignKey.set(
      userId,
      arr.filter((n) => n.id !== id),
    );
  }

  // Computed getters - automatically update when store changes
  // get needs to return a sorted list of notifications by read_at
  // notifications with no read_at should be at the top, as they are new and unread
  // the second sort after that should be by created_at
  get all() {
    return Array.from(this.store.values()).sort((a, b) => {
      if (!a.read_at) return -1;
      if (!b.read_at) return 1;
      return 0;
      // return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }

  hydrateLocal() {
    return hydrateFromWorker({
      tableName: 'notifications',
      entity: 'notifications',
      modelStore: notificationStore,
    });
  }
}

export const notificationStore = new NotificationStore();

declare global {
  interface Window {
    notificationStore: NotificationStore;
  }
}
